import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { Crop, SiteState, TotalNitrogenLegalNormFilter } from 'src/app/shared/models';
import { getCrops } from 'src/app/store/selectors';

@Component({
  selector: 'app-year-filter',
  templateUrl: './year-filter.component.html',
  standalone: false,
})
export class YearFilterComponent implements OnInit, OnDestroy, OnChanges {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly fb = inject(FormBuilder);

  @Input() filter: { [key: string]: string | number | string[] | number[] };
  @Input() cropEnabled: boolean;
  @Output() update = new EventEmitter();
  @Output() filterEnabled = new EventEmitter();
  @Output() filterReset = new EventEmitter();

  readonly emptyFilterData: TotalNitrogenLegalNormFilter = {
    year: null,
    cropId: null,
  };
  previousFilterData = this.emptyFilterData;
  filterForm = this.fb.group(this.previousFilterData);

  years: number[] = [-1, 0, 1].map((n) => new Date().getFullYear() + n);

  crops$: Observable<Crop[]> = this.store.pipe(select(getCrops));
  filteredCrops$ = combineLatest([this.filterForm.get('cropId').valueChanges.pipe(startWith('')), this.crops$]).pipe(
    filter(([value, _]) => typeof value === 'string'),
    map(([value, crops]) => (crops ? this.filterCrops(value as string, crops) : [])),
  );

  private subscriptions: Subscription[] = [];

  ngOnInit(): void {
    combineLatest([this.filterForm.get('year').valueChanges.pipe(startWith('')), this.filterForm.get('cropId').valueChanges.pipe(startWith(''))])
      .pipe(
        map(
          ([year, cropId]: [number, string | number]) =>
            ({
              year,
              cropId: typeof cropId === 'string' ? null : cropId,
            }) as TotalNitrogenLegalNormFilter,
        ),
      )
      .subscribe((filterData: TotalNitrogenLegalNormFilter) => {
        this.updateFilter(filterData);
      });
  }

  ngOnDestroy() {
    this.resetFilter();
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.filter) {
      const filterData = this.updateFilter({
        year: null,
        cropId: null,
        ...changes.filter.currentValue,
      });
      this.filterForm.setValue(filterData, { emitEvent: false });
    }
  }

  updateFilter(filterData: TotalNitrogenLegalNormFilter = this.emptyFilterData) {
    if (JSON.stringify(filterData) !== JSON.stringify(this.previousFilterData)) {
      const filterEnabled = Object.keys(filterData).filter((key) => !!key).length > 0;
      this.previousFilterData = filterData;
      this.update.emit(filterData);
      this.filterEnabled.emit(filterEnabled);
    }
    return filterData;
  }

  resetFilter() {
    this.filterForm.get('year').setValue(null);
    this.filterForm.get('cropId').setValue(null);
    this.filterReset.emit(true);
  }

  displayCrop$() {
    return this.crops$.pipe(
      map((crops) => (selectedCropId?: number) => {
        let foundCrop: Crop;
        if (selectedCropId) {
          foundCrop = crops.find((crop) => crop.id === selectedCropId);
        }
        return foundCrop ? foundCrop.name : '';
      }),
    );
  }

  private filterCrops(value: string, items: Crop[]): Crop[] {
    return [...items]
      .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
      .filter((option) => option.name.toLowerCase().includes(value.toLowerCase()));
  }
}
